import {
  FIELD_TYPE_CATALOGUE,
  type FieldFormat,
  type FieldType,
  type FilterCondition,
  type FilterOperator,
} from '@crm/contracts';
import { ChevronDown, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { FieldEditor } from '../fields/editor.tsx';
import { Button } from '../ui/button.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectTriggerClass,
} from '../ui/select.tsx';
import { stageTone, TONE_VARIANT } from '../../lib/value-tones.ts';
import { Badge } from '../ui/badge.tsx';
import { OptionSearch } from './option-search.tsx';

/**
 * The filter language in the UI (RELAY-941): one row per condition — field, operator, value —
 * where the operators come from the field's TYPE in the catalogue and the value editor is the
 * type's. The rows are ANDed into the shared filter DSL the search endpoint takes.
 */
export type StageGroup = 'open' | 'won' | 'lost';
export interface FilterOption {
  value: string;
  label: string;
  /** A stage's `StageGroup`, or the `OptionGroup` the option is listed under. */
  group?: string | undefined;
}

export interface FilterField {
  name: string;
  label: string;
  type: FieldType;
  /**
   * `group` puts a stage under Open, Won or Lost (neutral, positive, negative) in pipeline order;
   * options without groups are listed A→Z.
   */
  options?: ReadonlyArray<FilterOption> | undefined;
  /** How values read in chips and range inputs (a `$` before a rate, a custom field's format). */
  format?: FieldFormat | null | undefined;
  /** Shown as a chip beside the search input; the others are under "Add filter". */
  primary?: boolean | undefined;
  /** Values to suggest for free text (asked from 3 typed characters, debounced). */
  hints?: ((text: string) => Promise<string[]>) | undefined;
}

/** How an operator reads in chips and the builder: words, like Jira, never signs. */
export const OP_LABEL: Record<FilterOperator, string> = {
  eq: 'is',
  neq: 'is not',
  contains: 'contains',
  starts_with: 'begins with',
  gt: 'is more than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  in: 'is any of',
  not_in: 'is none of',
  is_empty: 'is empty',
  is_not_empty: 'has any value',
};
const NO_VALUE: FilterOperator[] = ['is_empty', 'is_not_empty'];

export function FilterBar({
  fields,
  conditions,
  onChange,
  addLabel = 'Add filter',
}: {
  fields: readonly FilterField[];
  conditions: FilterCondition[];
  onChange: (next: FilterCondition[]) => void;
  addLabel?: string;
}) {
  const update = (i: number, patch: Partial<FilterCondition>) =>
    onChange(conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const first = fields[0];
  return (
    <div className="flex flex-col gap-2" data-testid="filter-bar">
      {conditions.map((c, i) => {
        const field = fields.find((f) => f.name === c.field) ?? first;
        if (!field) return null;
        const spec = FIELD_TYPE_CATALOGUE[field.type];
        const listOp = c.op === 'in' || c.op === 'not_in';
        return (
          <div key={i} className="flex flex-wrap items-center gap-2" data-testid="filter-row">
            <Select
              value={c.field}
              onValueChange={(name) => {
                const f = fields.find((x) => x.name === name)!;
                update(i, {
                  field: name,
                  op: FIELD_TYPE_CATALOGUE[f.type].operators[0]!,
                  value: undefined,
                });
              }}
            >
              <SelectTrigger className="w-44" aria-label="Filter field">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {fields.map((f) => (
                  <SelectItem key={f.name} value={f.name}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={c.op}
              onValueChange={(op) =>
                update(i, {
                  op: op as FilterOperator,
                  value: NO_VALUE.includes(op as FilterOperator) ? undefined : c.value,
                })
              }
            >
              <SelectTrigger className="w-36" aria-label="Filter operator">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {spec.operators.map((op) => (
                  <SelectItem key={op} value={op}>
                    {OP_LABEL[op]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!NO_VALUE.includes(c.op) && (
              <div className="min-w-48 flex-1">
                {listOp && field.options?.length ? (
                  <OptionsDropdown
                    label={field.label}
                    options={field.options}
                    value={Array.isArray(c.value) ? (c.value as string[]) : []}
                    onChange={(v) => update(i, { value: v.length ? v : undefined })}
                  />
                ) : listOp && field.type !== 'multi_select' ? (
                  <FieldEditor
                    type={spec.hasOptions ? 'multi_select' : 'text'}
                    label="Filter values"
                    options={field.options}
                    value={
                      spec.hasOptions
                        ? c.value
                        : Array.isArray(c.value)
                          ? (c.value as string[]).join(', ')
                          : ''
                    }
                    onChange={(v) =>
                      update(i, {
                        value: spec.hasOptions
                          ? v
                          : typeof v === 'string'
                            ? v
                                .split(',')
                                .map((s) => s.trim())
                                .filter(Boolean)
                            : undefined,
                      })
                    }
                  />
                ) : field.options?.length ? (
                  // A field with a fixed list (a stage, a country on a text column) picks one from
                  // the same list as the quick filters: groups, pills, search.
                  <OptionsDropdown
                    single
                    label={field.label}
                    options={field.options}
                    value={typeof c.value === 'string' ? [c.value] : []}
                    onChange={(v) => update(i, { value: v[0] })}
                  />
                ) : (
                  <div className="flex items-center gap-1.5">
                    {field.format?.prefix && (
                      <span className="text-muted-foreground text-sm">{field.format.prefix}</span>
                    )}
                    <FieldEditor
                      type={field.type}
                      label="Filter value"
                      value={c.value ?? null}
                      onChange={(v) => update(i, { value: v ?? undefined })}
                    />
                    {field.format?.suffix && (
                      <span className="text-muted-foreground text-sm">{field.format.suffix}</span>
                    )}
                  </div>
                )}
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              aria-label="Remove filter"
              onClick={() => onChange(conditions.filter((_, j) => j !== i))}
            >
              <X />
            </Button>
          </div>
        );
      })}
      {first && (
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              onChange([
                ...conditions,
                { field: first.name, op: FIELD_TYPE_CATALOGUE[first.type].operators[0]! },
              ])
            }
            data-testid="add-filter"
          >
            <Plus /> {addLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

/** "Is any of" / "is none of" over a fixed list: a select-like trigger opening a searchable checklist. */
function OptionsDropdown({
  label,
  options,
  value,
  onChange,
  single,
}: {
  label: string;
  options: ReadonlyArray<FilterOption>;
  value: string[];
  onChange: (next: string[]) => void;
  /** "Is" / "is not": one choice, and picking it closes the list. */
  single?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const chosen = options.filter((o) => value.includes(o.value));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={selectTriggerClass}
        aria-label={single ? 'Filter value' : 'Filter values'}
        {...(chosen.length ? {} : { 'data-placeholder': '' })}
      >
        <span className="flex min-w-0 flex-wrap items-center gap-1">
          {chosen.length === 0
            ? 'Choose…'
            : chosen.some((o) => o.group)
              ? // Stages read as their group's pills, as in the lists.
                chosen.map((o) => (
                  <Badge key={o.value} variant={TONE_VARIANT[stageTone(o.group)]}>
                    {o.label}
                  </Badge>
                ))
              : chosen.map((o) => o.label).join(', ')}
        </span>
        <ChevronDown className="text-muted-foreground size-4" />
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2">
        <OptionSearch
          single={single}
          label={label}
          options={options}
          selected={value}
          onChange={(next) => {
            onChange(next);
            if (single) setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
